import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChatCircleDots, X, CaretRight } from "@phosphor-icons/react";
import { useOngoingChat } from "../context/OngoingChatContext";
import { resolveAssetUrl } from "../lib/api";

export default function OngoingChatDock() {
  const { activeChat, billing, lastMessage, unreadCount, clearActiveChat } = useOngoingChat();
  const location = useLocation();
  const navigate = useNavigate();

  if (!activeChat?.chat_id) return null;
  if (location.pathname === `/chat/${activeChat.chat_id}`) return null;
  if (location.pathname === "/auth" || location.pathname === "/astrologer/login") return null;

  // The dock surfaces for `ringing` (waiting for astrologer to pick up) AND
  // `active` (live in session). Each state needs different visuals — the
  // ringing pill must NOT say "Tap to continue the conversation" because
  // there's nothing to continue with yet, and there's no time-left to
  // show either. Once the astrologer accepts, ChatPage's ringing-poll
  // flips status → "active" and the dock automatically morphs.
  const isRinging = activeChat?.status === "ringing";

  // Live time-left from the most recent heartbeat (active chats only).
  let timeLeftLabel = null;
  let low = false;
  if (!isRinging && billing && billing.seconds_left !== undefined) {
    const heartbeatTs = billing._heartbeat_ts || Date.now();
    const secsSinceHeartbeat = Math.max(0, Math.floor((Date.now() - heartbeatTs) / 1000));
    const secsLeft = Math.max(0, (billing.seconds_left || 0) - secsSinceHeartbeat);
    const mmLeft = Math.floor(secsLeft / 60);
    const ssLeft = secsLeft % 60;
    timeLeftLabel = `${String(mmLeft).padStart(2, "0")}:${String(ssLeft).padStart(2, "0")}`;
    low = secsLeft <= 60;
  }

  const onReturn = () => navigate(`/chat/${activeChat.chat_id}`);

  // Last message preview line — strip the [Auto-reply] prefix for cleanliness
  const previewText = (() => {
    if (!lastMessage?.text) return "Tap to continue your reading…";
    const t = lastMessage.text.replace(/^\[Auto-reply\]\s*/, "").trim();
    if (lastMessage.sender_role === "customer") return `You: ${t}`;
    return t;
  })();

  const firstName = activeChat.astrologer_first_name || activeChat.astrologer_name || "Astrologer";

  return (
    <div
      className="fixed z-50 left-2 right-2 bottom-2 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      data-testid="ongoing-chat-dock"
    >
      <div
        className="bg-[#3F2D1B] text-[#FAF4E8] rounded-2xl shadow-2xl border border-amber-700/40 overflow-hidden animate-[dock-slide_320ms_ease-out]"
        role="button"
        tabIndex={0}
        onClick={onReturn}
        onKeyDown={(e) => { if (e.key === "Enter") onReturn(); }}
        data-testid="ongoing-chat-dock-return"
      >
        {/* Header strip: avatar + name + timer + close */}
        <div className="flex items-center gap-3 px-3 py-2.5 bg-gradient-to-r from-[#4a3624] to-[#3F2D1B] border-b border-amber-700/30">
          <div className="relative flex-shrink-0">
            <img
              src={resolveAssetUrl(activeChat.astrologer_avatar)}
              alt=""
              className="w-10 h-10 rounded-full object-cover border-2 border-amber-300/70"
            />
            <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-[#3F2D1B] ${isRinging ? "bg-amber-400 animate-ping-slow" : "bg-emerald-400 animate-pulse"}`} />
            {!isRinging && unreadCount > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-[#3F2D1B]"
                data-testid="ongoing-chat-dock-unread"
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <ChatCircleDots size={12} weight="fill" className="text-amber-300 flex-shrink-0" />
              <span className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
                {isRinging ? `Connecting · ${firstName}` : `Live with ${firstName}`}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${isRinging ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
              <span className="text-[11px] text-amber-100/80">
                {isRinging ? "Waiting for astrologer…" : "In session"}
              </span>
              {!isRinging && timeLeftLabel && (
                <span className={`font-mono text-[11px] font-semibold ${low ? "text-rose-300" : "text-amber-200"}`}>
                  · {timeLeftLabel} left
                </span>
              )}
            </div>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); clearActiveChat(); }}
            className="flex-shrink-0 p-1.5 rounded-full hover:bg-white/10 transition-colors"
            aria-label="Hide chat dock"
            data-testid="ongoing-chat-dock-hide"
            title="Hide for now (chat keeps running)"
          >
            <X size={14} />
          </button>
        </div>

        {/* Last message preview — hide while ringing (no live convo yet) */}
        {!isRinging && (
          <div className="px-4 py-3 bg-[#3F2D1B] flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-widest text-amber-300/70 mb-1">
                {lastMessage?.sender_role === "customer" ? "You said" : `${firstName} said`}
              </div>
              <p className="text-[13px] leading-snug text-[#FAF4E8] line-clamp-2 break-words">
                {previewText}
              </p>
            </div>
            <CaretRight size={16} className="text-amber-300 flex-shrink-0 mt-1" />
          </div>
        )}

        {/* CTA strip — copy + colour shift with the ringing/active state. */}
        <div
          className={`px-4 py-2.5 text-center font-semibold text-[13px] tracking-wide ${isRinging ? "bg-amber-300/90 text-[#3F2D1B]" : "bg-amber-500 text-[#1A1410]"}`}
          data-testid="ongoing-chat-dock-cta"
        >
          {isRinging ? "Connecting… we'll let you know the moment Sachin picks up" : "Tap to continue the conversation"}
        </div>
      </div>
      <style>{`
        @keyframes dock-slide {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 1; }
          75%, 100% { transform: scale(1.6); opacity: 0; }
        }
        .animate-ping-slow {
          animation: ping-slow 1.4s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
      `}</style>
    </div>
  );
}
